/* eslint-disable react-hooks/exhaustive-deps */
"use client";
import { useGetCompanyQuery } from "@/api/AppApi/services/company";
import { PaginationModes } from "@/api/AppApi/services/company/interfaces/ICompany";
import {
  useGetAllProductsInfiniteQuery,
  useGetAllProductsQuery,
} from "@/api/AppApi/services/product";
import ProductGrid from "@/app/components/common-builder/product/product-grids/ProductGrid";
import CustomButton, {
  BtnRadius,
  BtnSizes,
  BtnTypes,
} from "@/app/components/lib/CustomButton";
import { useTranslation } from "@/hooks/localization/useTranslations";
import { useEffect } from "react";
import { Spinner } from "react-bootstrap";
import { useInView } from "react-hook-inview";
import Paginate from "../pagination/Paginate";
import ProductContainer from "./ProductContainer";
import { Size } from "@/api/AppApi/common-interfaces/api-enums";
import SearchNoResult from "../../search-page/SearchNoResult";
import Filter from "@/app/components/Filter";
import ViewCategoryPixel from "../../category-page/ViewCategoryPixel";

interface props {
  categoryId?: number | string;
  searchParams: { [key: string]: string };
  isCategoryPage: boolean;
  PixeListName: string;
  PixeListId: string;
}

const productQuery = (query: any, category_id: number | string) => {
  const params = {
    ...query,
    limit: 24,
  };
  if (category_id) params["category_id"] = category_id;
  return params;
};

const ProductsWithFilters: React.FC<props> = ({
  categoryId,
  searchParams,
  isCategoryPage,
  PixeListName,
  PixeListId,
}) => {
  const t = useTranslation();
  const [ref, isInView] = useInView();
  const getCompanty = useGetCompanyQuery();
  const { pagination_mode } =
    getCompanty?.data?.result?.theme?.components?.body ?? {};

  const isInfiniteScroll = pagination_mode === PaginationModes?.scroll;
  const isButtonScroll = pagination_mode === PaginationModes?.button;

  const queryParams = { ...productQuery(searchParams, categoryId!) };

  const isEvent = queryParams?.sub_type === "event";

  const { data: PaginatProductsData, isFetching: isPaginatProductsLoading } =
    useGetAllProductsQuery({
      params: { ...queryParams },
      enabled: !isInfiniteScroll && !isButtonScroll,
    });
  const PaginatProducts = PaginatProductsData?.result?.data;

  const getProducts = useGetAllProductsInfiniteQuery({
    params: { ...queryParams },
    enabled: isInfiniteScroll || isButtonScroll,
  });
  const products =
    !isInfiniteScroll && !isButtonScroll
      ? PaginatProducts
      : getProducts?.data?.pages.map((page) => page.result?.data).flat()!;

  const isProductLoading = isPaginatProductsLoading ;
  const theme = getCompanty?.data?.result?.theme!;
  const size = isEvent
    ? Size.large
    : theme?.components?.product?.product_box_style!;

  const restoreScrollPosition = () => {
    const scrollPosition = sessionStorage.getItem("scrollPosition");
    if (scrollPosition) {
      window.scrollTo(0, parseInt(scrollPosition));
      sessionStorage.removeItem("scrollPosition");
    }
  };

  useEffect(() => {
    restoreScrollPosition();
  }, []);

  useEffect(() => {
    if (getProducts.hasNextPage && !getProducts.isFetchingNextPage) {
      getProducts.fetchNextPage();
    }
  }, [isInView, searchParams]);

  const q = searchParams.q ?? "";
  if (!isCategoryPage && !isProductLoading && products?.length === 0)
    return <SearchNoResult q={q} />;

  return (
    <>
      <Filter
        productNum={products?.length ?? 0}
        searchParams={searchParams}
        category_id={categoryId}
      />
      <ProductContainer
        productsLenght={products?.length || 1}
        loading={isProductLoading}
        isCategoryPage={!!isCategoryPage}
      >
        <ProductGrid
          products={{
            data: [...(products ?? [])],
            configurations: { style_type: size },
          }}
          theme={theme}
          showCustomEventData={isEvent}
        />
        {isInfiniteScroll ? (
          <div ref={ref} className="w-100 d-flex justify-content-center mt-3">
            {getProducts.hasNextPage && <Spinner />}
          </div>
        ) : isButtonScroll ? (
          <div className="mt-4 d-flex justify-content-center">
            {getProducts.hasNextPage && (
              <CustomButton
                style={{
                  background: "var(--add-to-cart-bg-color)",
                }}
                onClick={() => {
                  if (
                    getProducts.hasNextPage &&
                    !getProducts.isFetchingNextPage
                  ) {
                    getProducts.fetchNextPage();
                  }
                }}
                type={BtnTypes.primary}
                size={BtnSizes.medium}
                radius={BtnRadius.medium}
              >
                {!getProducts?.isFetching ? t.loadMore : <Spinner />}
              </CustomButton>
            )}
          </div>
        ) : (
          <Paginate
            pageCount={PaginatProductsData?.result?.meta?.last_page || 1}
          />
        )}
      </ProductContainer>
      {isCategoryPage && products && (
        <ViewCategoryPixel
          products={products}
          item_list_name={PixeListName}
          item_list_id={PixeListId}
        />
      )}
    </>
  );
};
export default ProductsWithFilters;
